import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import dashboard from 'api/axios/foka/dashboard'
import { generateKeys, zeroFill, zeroMetrics } from './chartUtil'
import { DashboardInfoTypes } from 'features/Codes'
import moment from 'moment'
import { getSavedChart, persistChart } from './chartStorage'
import { current } from 'immer'

const DashboardChartAPIs = {
  [DashboardInfoTypes.AD_ACCOUNT]: dashboard.getAccountChart,
  [DashboardInfoTypes.CAMPAIGN]: dashboard.getCampaignChart,
  [DashboardInfoTypes.AD_GROUP]: dashboard.getAdGroupChart,
  [DashboardInfoTypes.KEYWORD]: dashboard.getKeywordChart,
  [DashboardInfoTypes.CREATIVE]: dashboard.getCreativeChart,
  [DashboardInfoTypes.TOTAL_CAMPAIGN]: dashboard.getTotalCampaignChart,
  [DashboardInfoTypes.TOTAL_AD_GROUP]: dashboard.getTotalAdGroupChart,
  [DashboardInfoTypes.TOTAL_KEYWORD]: dashboard.getTotalKeywordChart,
  [DashboardInfoTypes.TOTAL_CONTENT_MATCHING]:
    dashboard.getTotalContentMatchingChart,
  [DashboardInfoTypes.TOTAL_CREATIVE]: dashboard.getTotalCreativeChart,
}
export const fetchChart = createAsyncThunk(
  'dashboard/fetchChart',
  async (noArgs, { getState }) => {
    const createdDate = getState().account.current?.createdDate
    const {
      info: { type, id },
      common: { from, to },
      filter: { filters },
      table: {
        campaignTable: { selectedIds: campaignIds },
        adGroupTable: { selectedIds: adGroupIds },
      },
    } = getState().dashboard
    const start = createdDate
      ? moment
          .max(moment(createdDate, 'YYYY-MM-DD[T]HH:mm:ss'), moment(from))
          .format(moment.HTML5_FMT.DATE)
      : from
    const api = DashboardChartAPIs[type]
    const {
      data: {
        result: { data: reports },
      },
    } = await api({
      id,
      from: start,
      to,
      filters,
      campaignIds,
      adGroupIds,
      period: 'day',
    })
    const {
      data: {
        result: { data: summary },
      },
    } = await api({
      id,
      from: start,
      to,
      filters,
      campaignIds,
      adGroupIds,
      period: 'none',
    })
    const keys = generateKeys(start, to)
    return {
      data: zeroFill(keys, reports),
      summary: summary.length > 0 ? summary[0] : zeroMetrics,
    }
  }
)
const getInitialState = () => ({
  isLoading: false,
  metricColumns: getSavedChart(),
  data: [],
  summary: {},
})

const dashboardChartSlice = createSlice({
  name: 'dashboard/chart',
  initialState: getInitialState(),

  reducers: {
    setMetricColumn: (state, { payload: { index, metricId } }) => {
      state.metricColumns[index] = metricId
      const copy = current(state.metricColumns)
      persistChart(copy)
    },
    reset: () => getInitialState(),
  },

  extraReducers: builder => {
    builder.addCase(fetchChart.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      fetchChart.fulfilled,
      (state, { payload: { data, summary } }) => {
        state.isOutdated = false
        state.isLoading = false
        state.data = data
        state.summary = summary
      }
    )

    builder.addCase(fetchChart.rejected, state => {
      state.isLoading = false
    })
  },
})
export const { setMetricColumn, reset } = dashboardChartSlice.actions

export default dashboardChartSlice.reducer
